import { createContext, useContext, useState, useEffect, ReactNode, useCallback, useRef } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import i18n from "@/i18n";
import { localeToLang, langToLocale } from "@/i18n";
import { LANGUAGE_STORAGE_KEY, normalizeLanguage } from "@/config/i18n";

export type AccentColor = "violet" | "blue" | "emerald" | "rose" | "amber" | "cyan" | "red" | "yellow" | "orange" | "purple" | "teal" | "pink" | "lime" | "indigo" | "fuchsia" | "sky";
export type ButtonShape = "rounded" | "square";
export type SpeechRate = "slow" | "normal" | "fast";
export type VoiceOption =
  | "boy" | "girl" | "man" | "woman"
  // French
  | "claire" | "anais" | "antoine" | "genevieve" | "louis" | "paul"
  | "danick" | "teddy" | "nicolas"
  // English
  | "maria" | "joseph" | "bella" | "roger" | "sarah" | "laura" | "charlie"
  | "george" | "callum" | "river" | "harry" | "liam" | "alice" | "matilda"
  | "will" | "jessica" | "eric" | "chris" | "brian" | "daniel" | "lily"
  | "adam" | "bill";

export const LANGUAGES = [
  { code: "fr-FR", label: "🇫🇷 Français" },
  { code: "en-US", label: "🇺🇸 English" },
  { code: "en-GB", label: "🇬🇧 English (UK)" },
  { code: "es-ES", label: "🇪🇸 Español" },
  { code: "pt-BR", label: "🇧🇷 Português (BR)" },
  { code: "pt-PT", label: "🇵🇹 Português (PT)" },
  { code: "de-DE", label: "🇩🇪 Deutsch" },
  { code: "it-IT", label: "🇮🇹 Italiano" },
  { code: "nl-NL", label: "🇳🇱 Nederlands" },
  { code: "ru-RU", label: "🇷🇺 Русский" },
  { code: "ar-SA", label: "🇸🇦 العربية" },
  { code: "zh-CN", label: "🇨🇳 中文 (简体)" },
  { code: "zh-TW", label: "🇹🇼 中文 (繁體)" },
  { code: "ja-JP", label: "🇯🇵 日本語" },
  { code: "ko-KR", label: "🇰🇷 한국어" },
  { code: "hi-IN", label: "🇮🇳 हिन्दी" },
  { code: "tr-TR", label: "🇹🇷 Türkçe" },
  { code: "pl-PL", label: "🇵🇱 Polski" },
  { code: "sv-SE", label: "🇸🇪 Svenska" },
  { code: "da-DK", label: "🇩🇰 Dansk" },
  { code: "fi-FI", label: "🇫🇮 Suomi" },
  { code: "no-NO", label: "🇳🇴 Norsk" },
  { code: "cs-CZ", label: "🇨🇿 Čeština" },
  { code: "el-GR", label: "🇬🇷 Ελληνικά" },
  { code: "he-IL", label: "🇮🇱 עברית" },
  { code: "th-TH", label: "🇹🇭 ภาษาไทย" },
  { code: "vi-VN", label: "🇻🇳 Tiếng Việt" },
  { code: "id-ID", label: "🇮🇩 Bahasa Indonesia" },
  { code: "ms-MY", label: "🇲🇾 Bahasa Melayu" },
  { code: "ro-RO", label: "🇷🇴 Română" },
  { code: "uk-UA", label: "🇺🇦 Українська" },
  { code: "hu-HU", label: "🇭🇺 Magyar" },
  { code: "sw-KE", label: "🇰🇪 Kiswahili" },
  { code: "yo-NG", label: "🇳🇬 Yorùbá" },
  { code: "wo-SN", label: "🇸🇳 Wolof" },
  { code: "ln-CD", label: "🇨🇩 Lingála" },
];

export type AppSettings = {
  accentColor: AccentColor;
  buttonShape: ButtonShape;
  voice: VoiceOption;
  language: string;
  voiceEnabled: boolean;
  speechRate: SpeechRate;
  lastRoute: string;
};

type AppSettingsCtx = AppSettings & {
  setAccentColor: (c: AccentColor) => void;
  setButtonShape: (s: ButtonShape) => void;
  setVoice: (v: VoiceOption) => void;
  setLanguage: (l: string) => void;
  setVoiceEnabled: (v: boolean) => void;
  setSpeechRate: (r: SpeechRate) => void;
  setLastRoute: (r: string) => void;
  saveSettings: () => Promise<void>;
  saving: boolean;
  dirty: boolean;
  settingsLoaded: boolean;
};

const STORAGE_KEY = "kelas-app-settings";

// La langue par défaut suit la préférence i18n déjà persistée (localStorage / géo),
// afin de ne jamais écraser un choix utilisateur au démarrage.
const defaultLanguageLocale = (): string => {
  if (typeof window === "undefined") return "fr-FR";
  const stored = localStorage.getItem(LANGUAGE_STORAGE_KEY);
  const code = normalizeLanguage(stored || i18n.language);
  return langToLocale[code] || "fr-FR";
};

const defaults: AppSettings = {
  accentColor: "violet",
  buttonShape: "rounded",
  voice: "lily",
  language: defaultLanguageLocale(),
  voiceEnabled: false,
  speechRate: "normal",
  lastRoute: "/",
};

const accentPalettes: Record<AccentColor, { primary: string; primaryForeground: string }> = {
  // Primaires
  red: { primary: "0 84% 60%", primaryForeground: "0 0% 100%" },
  blue: { primary: "217 91% 60%", primaryForeground: "0 0% 100%" },
  yellow: { primary: "48 96% 53%", primaryForeground: "0 0% 10%" },
  // Mélangées
  violet: { primary: "262 83% 58%", primaryForeground: "0 0% 100%" },
  emerald: { primary: "160 84% 39%", primaryForeground: "0 0% 100%" },
  rose: { primary: "347 77% 50%", primaryForeground: "0 0% 100%" },
  amber: { primary: "38 92% 50%", primaryForeground: "0 0% 10%" },
  cyan: { primary: "188 94% 43%", primaryForeground: "0 0% 100%" },
  orange: { primary: "25 95% 53%", primaryForeground: "0 0% 100%" },
  purple: { primary: "271 76% 53%", primaryForeground: "0 0% 100%" },
  teal: { primary: "173 80% 40%", primaryForeground: "0 0% 100%" },
  pink: { primary: "330 81% 60%", primaryForeground: "0 0% 100%" },
  lime: { primary: "84 81% 44%", primaryForeground: "0 0% 10%" },
  indigo: { primary: "239 84% 67%", primaryForeground: "0 0% 100%" },
  fuchsia: { primary: "292 84% 61%", primaryForeground: "0 0% 100%" },
  sky: { primary: "199 89% 48%", primaryForeground: "0 0% 100%" },
};

const shapeRadii: Record<ButtonShape, string> = {
  rounded: "0.75rem",
  square: "0.25rem",
};

const AppSettingsContext = createContext<AppSettingsCtx>({
  ...defaults,
  setAccentColor: () => {},
  setButtonShape: () => {},
  setVoice: () => {},
  setLanguage: () => {},
  setVoiceEnabled: () => {},
  setSpeechRate: () => {},
  setLastRoute: () => {},
  saveSettings: async () => {},
  saving: false,
  dirty: false,
  settingsLoaded: false,
});

function applySettings(settings: AppSettings) {
  const root = document.documentElement;
  const palette = accentPalettes[settings.accentColor];
  root.style.setProperty("--primary", palette.primary);
  root.style.setProperty("--primary-foreground", palette.primaryForeground);
  root.style.setProperty("--radius", shapeRadii[settings.buttonShape]);

  // Build gradient-hero from the accent color
  const [h, s, l] = palette.primary.split(" ").map((v) => parseFloat(v));
  const gradientHero = `linear-gradient(135deg, hsl(${h} ${s}% ${l}%) 0%, hsl(${h + 23} ${s}% ${l + 8}%) 60%, hsl(${h + 45} ${Math.max(s - 7, 0)}% ${l + 4}%) 100%)`;
  root.style.setProperty("--gradient-hero", gradientHero);
  root.style.setProperty("--shadow-primary", `0 8px 32px -8px hsl(${h} ${s}% ${l}% / 0.35)`);
}

export const AppSettingsProvider = ({ children }: { children: ReactNode }) => {
  const { user } = useAuth();
  const [settings, setSettings] = useState<AppSettings>(() => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      const initial = stored ? { ...defaults, ...JSON.parse(stored) } : defaults;
      // Apply immediately to avoid theme/voice flash before first render
      try { applySettings(initial); } catch { /* ignore */ }
      return initial;
    } catch {
      return defaults;
    }
  });
  const [saving, setSaving] = useState(false);
  const [savedSettings, setSavedSettings] = useState<AppSettings>(settings);
  // If we have cached settings, mark as loaded immediately so the UI is usable
  // without waiting for the DB round-trip. The DB sync will run in the background.
  const [settingsLoaded, setSettingsLoaded] = useState<boolean>(() => {
    try { return !!localStorage.getItem(STORAGE_KEY); } catch { return false; }
  });
  const loadedRef = useRef(false);

  // Load from DB when user logs in, reset to defaults on logout
  useEffect(() => {
    if (!user) {
      loadedRef.current = false;
      setSettingsLoaded(false);
      setSettings(defaults);
      setSavedSettings(defaults);
      localStorage.removeItem(STORAGE_KEY);
      return;
    }
    if (loadedRef.current) return;
    loadedRef.current = true;

    supabase
      .from("user_app_settings" as any)
      .select("accent_color, button_shape, voice, language, voice_enabled, speech_rate, last_route")
      .eq("user_id", user.id)
      .maybeSingle()
      .then(({ data }: any) => {
        if (data) {
          const loaded: AppSettings = {
            accentColor: data.accent_color || defaults.accentColor,
            buttonShape: data.button_shape || defaults.buttonShape,
            voice: data.voice || defaults.voice,
            language: data.language || defaults.language,
            voiceEnabled: data.voice_enabled !== undefined ? data.voice_enabled : defaults.voiceEnabled,
            speechRate: (data.speech_rate as SpeechRate) || defaults.speechRate,
            lastRoute: data.last_route || defaults.lastRoute,
          };
          setSettings(loaded);
          setSavedSettings(loaded);
          localStorage.setItem(STORAGE_KEY, JSON.stringify(loaded));
        }
        setSettingsLoaded(true);
      });
  }, [user]);


  useEffect(() => {
    applySettings(settings);
    localStorage.setItem(STORAGE_KEY, JSON.stringify(settings));
    // Sync i18n language
    const lang = localeToLang[settings.language] || settings.language.split("-")[0] || "fr";
    if (i18n.language !== lang) {
      i18n.changeLanguage(lang);
      localStorage.setItem(LANGUAGE_STORAGE_KEY, lang);
    }
  }, [settings]);

  const dirty = JSON.stringify(settings) !== JSON.stringify(savedSettings);

  const setAccentColor = useCallback((c: AccentColor) => setSettings((s) => ({ ...s, accentColor: c })), []);
  const setButtonShape = useCallback((s: ButtonShape) => setSettings((prev) => ({ ...prev, buttonShape: s })), []);
  const setVoice = useCallback((v: VoiceOption) => setSettings((s) => ({ ...s, voice: v })), []);
  const setLanguage = useCallback((l: string) => setSettings((s) => ({ ...s, language: l })), []);
  const setVoiceEnabled = useCallback((v: boolean) => setSettings((s) => ({ ...s, voiceEnabled: v })), []);
  const setSpeechRate = useCallback((r: SpeechRate) => setSettings((s) => ({ ...s, speechRate: r })), []);
  const setLastRoute = useCallback((r: string) => setSettings((s) => ({ ...s, lastRoute: r })), []);

  const saveSettings = useCallback(async () => {
    if (!user) return;
    setSaving(true);
    try {
      const payload = {
        user_id: user.id,
        accent_color: settings.accentColor,
        button_shape: settings.buttonShape,
        voice: settings.voice,
        language: settings.language,
        voice_enabled: settings.voiceEnabled,
        speech_rate: settings.speechRate,
        last_route: settings.lastRoute,
      };
      // Upsert
      await (supabase as any).from("user_app_settings").upsert(payload, { onConflict: "user_id" });
      setSavedSettings({ ...settings });
    } finally {
      setSaving(false);
    }
  }, [user, settings]);

  return (
    <AppSettingsContext.Provider value={{ ...settings, setAccentColor, setButtonShape, setVoice, setLanguage, setVoiceEnabled, setSpeechRate, setLastRoute, saveSettings, saving, dirty, settingsLoaded }}>
      {children}
    </AppSettingsContext.Provider>
  );
};

export const useAppSettings = () => useContext(AppSettingsContext);
